import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { VOICES, VOICES_ORDER, VOICES_QUALITY_PROMISES } from "../data/voices";
import OptimizedImage from "../components/media/OptimizedImage";
import { ArrowRight, Sparkles, Shield, Users, Check } from "lucide-react";

/**
 * SAT Stimmen™ Übersichtsseite — /stimmen
 * Zeigt alle 6 Produkte + Positionierung + Prinzipien.
 */
const VoicesIndex = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#050b1c] via-[#081328] to-[#050a18] text-white pt-28 pb-24" data-testid="voices-index-page">
      <Helmet>
        <title>SAT Stimmen™ · Sechs Räume für deine Stimme</title>
        <meta
          name="description"
          content="SAT Stimmen™ — sechs Bildungsformate für Frauen, Männer, Mütter, Väter, Kinder und Eltern. Premium-Räume für bewussten Ausdruck. Preis auf Anfrage."
        />
      </Helmet>

      <div className="container-sat max-w-7xl mx-auto px-6">
        {/* Hero */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.32em] uppercase text-[#D4AF37] mb-6">
            <Sparkles size={12} /> SAT Stimmen™
          </div>
          <h1 className="serif text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.02] mb-8">
            Sechs Räume.<br />
            <em className="italic text-[#D4AF37]">Eine Stimme.</em>
          </h1>
          <p className="max-w-2xl mx-auto text-lg text-white/70 leading-relaxed">
            Sechs eigenständige Premium-Bildungsformate — jedes ein sorgfältig kuratierter Raum
            für einen Aspekt deiner Stimme: als Frau, als Mann, als Mutter, als Vater, als Kind,
            als Eltern gemeinsam.
          </p>
          <p className="max-w-xl mx-auto text-sm text-white/50 mt-4 leading-relaxed">
            Bildungsformate — keine Therapie. Preis auf Anfrage — nach persönlichem Klärungsgespräch.
          </p>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-24">
          {VOICES_ORDER.map(slug => {
            const v = VOICES[slug];
            return (
              <Link
                key={slug}
                to={`/stimmen/${slug}`}
                data-testid={`voice-card-${slug}`}
                className="group relative rounded-2xl border border-white/10 transition-all duration-500 hover:border-white/25 hover:-translate-y-1 overflow-hidden flex flex-col"
                style={{ background: v.bg }}
              >
                {/* Cover thumbnail — 3:2 · dezenter Verlauf-Overlay */}
                {v.hero_asset && (
                  <div className="relative">
                    <OptimizedImage
                      assetId={v.hero_asset}
                      ratio="3/2"
                      overlayClassName="bg-gradient-to-b from-transparent via-transparent to-[#0e0512]/95"
                    />
                  </div>
                )}

                <div className="relative p-8 flex flex-col flex-1">
                  <div
                    className="absolute -top-[1px] left-8 right-8 h-[2px] rounded-full opacity-80 group-hover:opacity-100 transition"
                    style={{ background: `linear-gradient(90deg, transparent, ${v.accent}, transparent)` }}
                  />

                  <div className="text-[10px] tracking-[0.28em] uppercase mb-4" style={{ color: v.accent }}>
                    {v.kicker}
                  </div>

                  <h2 className="serif text-2xl sm:text-3xl font-light leading-tight mb-4 text-white/95">
                    {v.title} <em className="italic" style={{ color: v.accent }}>{v.title2}</em>
                  </h2>

                  <p className="text-sm text-white/60 leading-relaxed mb-6 line-clamp-4 flex-1">
                    {v.headline}
                  </p>

                  {v.is_minor_gated && (
                    <div className="inline-flex items-center gap-1.5 text-[10px] text-amber-300 mb-3 tracking-wider uppercase">
                      <Shield size={11} /> Kinderschutz · Elterneinwilligung
                    </div>
                  )}

                  <div className="inline-flex items-center gap-2 text-sm font-medium mt-auto" style={{ color: v.accent }}>
                    {v.label.replace("SAT ", "")} entdecken
                    <ArrowRight size={14} className="group-hover:translate-x-1 transition" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Quality Promises */}
        <div className="border-t border-white/10 pt-16">
          <div className="text-center mb-12">
            <div className="text-[11px] tracking-[0.32em] uppercase text-[#D4AF37] mb-4">
              Unser Versprechen
            </div>
            <h2 className="serif text-3xl sm:text-4xl font-light">
              Vier Prinzipien, an die wir uns halten.
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VOICES_QUALITY_PROMISES.map((q, i) => (
              <div
                key={i}
                className="p-6 rounded-xl border border-white/10 bg-white/[0.02]"
                data-testid={`voice-quality-${i}`}
              >
                <Check size={18} className="text-[#D4AF37] mb-3" />
                <div className="serif text-lg font-light text-white mb-2">{q.t}</div>
                <p className="text-sm text-white/60 leading-relaxed">{q.d}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-20 text-center">
          <Link
            to="/erstgespraech"
            data-testid="voices-index-cta"
            className="inline-flex items-center gap-2 rounded-full px-8 py-4 bg-[#D4AF37] text-[#081328] font-medium tracking-wider hover:shadow-[0_20px_60px_-15px_rgba(212,175,55,0.5)] transition"
          >
            <Users size={16} /> Unsicher, was zu dir passt? Erstgespräch buchen
          </Link>
        </div>
      </div>
    </div>
  );
};

export default VoicesIndex;
